import Cookies from 'js-cookie';

type EventSourcePolyfillOptions = {
  method?: string;
  headers?: Record<string, string>;
  body?: BodyInit | null;
};

export type MessageEventLike = {
  data: string;
  type?: string;
};

type SSEFrame = {
  data: string;
  event?: string;
};

/**
 * 基于 fetch 的 SSE 轻量封装。
 * 支持 POST/body/header，并把具名事件统一转发到 onmessage / addEventListener。
 */
export class EventSourcePolyfill {
  private controller = new AbortController();
  private listeners: Record<string, Set<(event: MessageEventLike) => void>> = {};
  private closed = false;

  public onmessage?: (event: MessageEventLike) => void;
  public onerror?: (err: any) => void;

  constructor(url: string, options: EventSourcePolyfillOptions = {}) {
    this.connect(url, options);
  }

  close(): void {
    if (this.closed) {
      return;
    }

    this.closed = true;
    this.controller.abort();
  }

  addEventListener(type: string, handler: (event: MessageEventLike) => void): void {
    if (!this.listeners[type]) {
      this.listeners[type] = new Set();
    }
    this.listeners[type].add(handler);
  }

  removeEventListener(type: string, handler: (event: MessageEventLike) => void): void {
    this.listeners[type]?.delete(handler);
  }

  private async connect(url: string, options: EventSourcePolyfillOptions): Promise<void> {
    try {
      const token = Cookies.get('token');
      const response = await fetch(url, {
        method: options.method || 'GET',
        headers: {
          Accept: 'text/event-stream',
          'Cache-Control': 'no-cache',
          ...options.headers,
        },
        body: options.body ?? null,
        credentials: 'include',
        signal: this.controller.signal,
      });

      if (!response.ok) {
        throw await this.buildHttpError(response);
      }

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('text/event-stream')) {
        const text = await response.text();
        this.emitEvent('message', {
          data: text || JSON.stringify({ type: 'done', data: '' }),
          type: 'message',
        });
        return;
      }

      if (!response.body) {
        throw new Error('ReadableStream not supported in response');
      }

      await this.readStream(response.body);
    } catch (error: any) {
      if (error?.name === 'AbortError' && this.closed) {
        return;
      }

      this.close();
      this.onerror?.(error);
    }
  }

  private async readStream(stream: ReadableStream<Uint8Array>): Promise<void> {
    const reader = stream.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';

    while (!this.closed) {
      const { value, done } = await reader.read();
      if (done) {
        buffer += decoder.decode();
        this.flushBuffer(buffer, true);
        this.emitEvent('done', { data: JSON.stringify({ type: 'done', data: '' }), type: 'done' });
        return;
      }

      buffer += decoder.decode(value, { stream: true });
      buffer = this.flushBuffer(buffer, false);
    }
  }

  private emitEvent(type: string, event: MessageEventLike): void {
    if (this.closed) {
      return;
    }

    this.listeners[type]?.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error(`Error in ${type} listener:`, error);
      }
    });

    this.onmessage?.({ ...event, type });
  }

  private flushBuffer(buffer: string, flushAll: boolean): string {
    const normalized = buffer.replace(/\r\n/g, '\n');
    const frames = normalized.split('\n\n');
    const remainder = flushAll ? '' : (frames.pop() ?? '');

    frames.forEach(frame => {
      const parsed = this.parseFrame(frame);
      if (!parsed) {
        return;
      }

      const eventType = parsed.event || 'message';
      this.emitEvent(eventType, { data: parsed.data, type: eventType });
    });

    return remainder;
  }

  private parseFrame(frame: string): SSEFrame | null {
    const lines = frame.split('\n');
    const dataLines: string[] = [];
    let eventName = '';

    lines.forEach(line => {
      if (!line || line.startsWith(':')) {
        return;
      }

      if (line.startsWith('event:')) {
        eventName = line.slice(6).trim();
        return;
      }

      if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trimStart());
      }
    });

    if (dataLines.length === 0) {
      return null;
    }

    return {
      event: eventName || undefined,
      data: dataLines.join('\n'),
    };
  }

  private async buildHttpError(response: Response): Promise<Error> {
    const contentType = response.headers.get('content-type') || '';
    let message = `HTTP error! status: ${response.status}`;

    try {
      if (contentType.includes('application/json')) {
        const errorJson = await response.json();
        message = errorJson?.msg || errorJson?.message || errorJson?.errorMessage || message;
      } else {
        const text = await response.text();
        if (text) {
          message = text;
        }
      }
    } catch (error) {
      // ignore parse errors
    }

    const err = new Error(message);
    (err as any).status = response.status;
    return err;
  }
}

export function parseString(data: any): any {
  if (typeof data !== 'string') return data;

  try {
    const parsed = JSON.parse(data);
    if (Array.isArray(parsed) || typeof parsed === 'object') {
      return parsed;
    }
  } catch (e) {
    return data;
  }

  return data;
}
